import React, { useEffect, useState } from "react";
import {
  Heart, MapPin, ExternalLink, Star, X, Share2, Link2, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { addLocalBookmark, encodeCardId, getLocalBookmarks } from "@/lib/bookmarks";
import PhotoCarousel from "@/components/PhotoCarousel";
import CardImage from "@/components/CardImage";

/**
 * Full card-detail modal opened by tapping the body of a grid card on
 * `/explore`. Reuses `<PhotoCarousel />` for the image gallery and
 * `<CardImage />` for the empty state. Grid-only for now — the swipe
 * deck keeps its existing single-card flow.
 *
 * Buttons + behavior:
 *   • Book Now     — same routing as the grid card's Book pill
 *                    (delegates to the parent-supplied `onBook`).
 *   • Save         — same optimistic-bookmark path as the grid card.
 *   • Share        — native share sheet when available, else copies
 *                    the deep-link `/explore?card=<id>` to clipboard.
 *   • Copy link    — dedicated button that always copies.
 *   • Close (X)    — closes the modal and strips `?card=` from URL.
 *
 * Deep-link support: `?card=<id>` in the URL auto-opens this modal
 * (handled by `ExploreGrid`, which passes the resolved card here).
 */

// Same placeholder-URL blocklist ExploreGrid uses so the carousel
// doesn't render backend-served "photo coming soon" seed URLs.
function _cleanPhotos(card) {
  const raw = card?.images || card?.metadata?.photos || [];
  return raw.filter((u) => {
    if (!u || typeof u !== "string") return false;
    const l = u.toLowerCase();
    return !l.includes("loremflickr.com") && !l.includes("placehold.co");
  });
}

function _deepLink(card) {
  if (typeof window === "undefined") return "";
  // Bot-crawlable share URL — the backend serves this route with
  // populated <meta og:*> tags so Facebook / Twitter / iMessage /
  // Slack / Google render rich previews. Humans hitting the URL get
  // meta-refreshed to /explore?card=<id> (0ms perceived delay; the
  // SPA modal auto-opens via the existing ?card= handler).
  const origin = window.location.origin;
  const id = encodeURIComponent(card?.id || "");
  return `${origin}/api/share/card/${id}`;
}

export default function CardDetailDialog({ card, onClose, onBook }) {
  const [saved, setSaved] = useState(false);
  // 2026-07 Pass B — optional check-in / check-out date state.
  // Ephemeral form state, NOT persisted. Only forwarded to bookNow
  // when BOTH dates are set (partial state is intentionally ignored).
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");

  useEffect(() => {
    if (!card) return;
    try {
      setSaved(getLocalBookmarks().some((b) => b.id === card.id));
    } catch {  /* noop */ }
    // Lock background scroll while the modal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [card]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!card) return null;

  const photos = _cleanPhotos(card);
  const loc = card.location || {};
  const locationStr = [loc.city, loc.state, loc.country, loc.port, loc.destination]
    .filter(Boolean).join(", ");
  const rating = card.rating || card.metadata?.rating;
  const currSymbol = card.currency === "EUR" ? "\u20AC" : "$";

  const save = () => {
    if (saved) return;
    setSaved(true);
    addLocalBookmark(card);
    api.post(`/destinations/${encodeCardId(card.id)}/swipe`, { direction: "save" })
      .catch(() => {});
    toast.success(`${card.name} saved \u2726`);
  };

  const book = ({ checkIn: ci, checkOut: co } = {}) => {
    // Forward dates to the parent's onBook — parent then calls
    // bookNow({ card, checkIn, checkOut }). Only both-set is honored
    // downstream (bookNow drops partial state before hitting the API).
    onBook?.(card, { checkIn: ci, checkOut: co });
  };

  const copyLink = async () => {
    const url = _deepLink(card);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      // Fallback: create a hidden textarea + document.execCommand copy.
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Link copied to clipboard");
      } catch {
        toast.error("Couldn't copy link");
      }
    }
  };

  const share = async () => {
    const url = _deepLink(card);
    // Native share sheet when available (mobile Safari, Android). Fall
    // back to clipboard copy on desktop / unsupported browsers.
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: card.name, text: card.name, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    copyLink();
  };

  const chips = [
    card.primary_filter || card.content_type || card.category,
    ...((card.categories || []).slice(0, 3)),
    card.vibe,
  ].filter(Boolean).filter((c, i, arr) => arr.indexOf(c) === i);

  const descriptionEmpty = !((card.description || "").trim());

  return (
    <div
      data-testid="card-detail-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={card.name}
      onClick={(e) => { if (e.currentTarget === e.target) onClose?.(); }}
      className="fixed inset-0 z-[120] bg-tb-navy-deep/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-8"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-tb-cream rounded-2xl shadow-2xl relative animate-in fade-in zoom-in-95 duration-200"
      >
        <button
          type="button"
          data-testid="card-detail-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 text-tb-navy flex items-center justify-center shadow tb-tap hover:brightness-105"
        >
          <X className="w-4.5 h-4.5" />
        </button>

        {/* Photo carousel — fall back to CardImage placeholder when the
            card has no gallery. */}
        <div className="p-3 pb-0">
          {photos.length > 0 ? (
            <PhotoCarousel photos={photos} testId="card-detail-carousel" />
          ) : (
            <div className="rounded-2xl overflow-hidden border-2 border-tb-cream-3 aspect-[16/10]">
              <CardImage card={card} className="w-full h-full" size="lg" />
            </div>
          )}
        </div>

        <div className="p-4 md:p-6 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <h2
                data-testid="card-detail-name"
                className="font-serif-it italic text-tb-navy text-2xl md:text-3xl leading-tight"
              >
                {card.name}
              </h2>
              {locationStr && (
                <p className="mt-1 flex items-center gap-1 font-hand text-sm text-tb-navy/65">
                  <MapPin className="w-3.5 h-3.5" /> {locationStr}
                </p>
              )}
              {rating && (
                <p className="mt-0.5 flex items-center gap-1 font-hand text-xs text-tb-navy/70">
                  <Star className="w-3 h-3 text-tb-gold" fill="currentColor" strokeWidth={0} />
                  {Number(rating).toFixed(1)}
                </p>
              )}
            </div>
            {card.price_from != null && (
              <div className="flex flex-col items-end leading-tight">
                <span className="font-serif text-xl md:text-2xl font-semibold text-tb-navy">
                  {currSymbol}{Math.round(card.price_from).toLocaleString()}
                </span>
                {card.price_label && (
                  <span className="font-hand text-xs text-tb-navy/55">{card.price_label}</span>
                )}
              </div>
            )}
          </div>

          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {chips.map((c) => (
                <span
                  key={c}
                  className="px-2 py-0.5 rounded-full bg-tb-navy/5 border border-tb-navy/10 font-hand text-[11px] text-tb-navy/75"
                >
                  {c}
                </span>
              ))}
            </div>
          )}

          <div
            data-testid="card-detail-description"
            className={
              "font-serif text-tb-navy/80 leading-relaxed text-sm md:text-base " +
              (descriptionEmpty ? "italic text-tb-navy/50" : "")
            }
          >
            {descriptionEmpty
              ? "Description coming soon \u2014 help us add one by contributing your review after your trip."
              : card.description}
          </div>

          <div className="pt-3 border-t border-tb-cream-3">
            {/* 2026-07 Pass B — larger circular action row. Mobile-app
                parity: 48px diameter buttons, single-tap targets, icon-only.
                Book Now still gets a distinct gold-ring treatment; save/
                share/close are cream tiles with subtle borders. */}
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                data-testid="card-detail-close-btn"
                onClick={onClose}
                aria-label="Close"
                className="w-12 h-12 rounded-full bg-white text-tb-navy border-2 border-tb-cream-3 hover:border-tb-navy/40 flex items-center justify-center tb-tap shadow-sm"
              >
                <X className="w-5 h-5" />
              </button>
              <button
                type="button"
                data-testid="card-detail-save-btn"
                onClick={save}
                aria-label={saved ? "Remove from Saved" : "Save"}
                aria-pressed={saved}
                className={
                  "w-12 h-12 rounded-full flex items-center justify-center tb-tap shadow-sm border-2 " +
                  (saved
                    ? "bg-tb-forest text-white border-tb-forest"
                    : "bg-white text-tb-forest border-tb-forest hover:bg-tb-forest-soft")
                }
              >
                <Heart className="w-5 h-5" fill={saved ? "currentColor" : "none"} />
              </button>
              <button
                type="button"
                data-testid="card-detail-share-btn"
                onClick={share}
                aria-label="Share"
                className="w-12 h-12 rounded-full bg-white text-tb-navy border-2 border-tb-cream-3 hover:border-tb-navy/40 flex items-center justify-center tb-tap shadow-sm"
              >
                <Share2 className="w-5 h-5" />
              </button>
              <button
                type="button"
                data-testid="card-detail-book-btn"
                onClick={() => book({ checkIn, checkOut })}
                aria-label="Book Now"
                className="w-14 h-14 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold flex items-center justify-center tb-tap shadow-md hover:brightness-110"
              >
                <ExternalLink className="w-6 h-6" />
              </button>
            </div>

            {/* Optional check-in / check-out inputs below Book Now.
                No persistence — passed straight to bookNow() when both
                are set. Backend (build_booking_url_with_dest) will
                include them as `checkin=YYYY-MM-DD&checkout=YYYY-MM-DD`
                on the Booking.com URL for dated-search conversion boost. */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="font-hand text-xs text-tb-navy/70">
                  Check-in (optional)
                </span>
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  data-testid="card-detail-checkin"
                  className="h-10 px-3 rounded-lg bg-tb-cream border-2 border-tb-navy/20 text-tb-navy font-hand text-sm focus:border-tb-gold focus:outline-none focus:ring-2 focus:ring-tb-gold/25"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-hand text-xs text-tb-navy/70">
                  Check-out (optional)
                </span>
                <input
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  min={checkIn || undefined}
                  data-testid="card-detail-checkout"
                  className="h-10 px-3 rounded-lg bg-tb-cream border-2 border-tb-navy/20 text-tb-navy font-hand text-sm focus:border-tb-gold focus:outline-none focus:ring-2 focus:ring-tb-gold/25"
                />
              </label>
            </div>
            <p className="mt-1.5 font-hand text-[11px] text-tb-navy/55 text-center">
              Prefill your dates on Booking.com for faster booking
            </p>

            <button
              type="button"
              data-testid="card-detail-copy-link-btn"
              onClick={copyLink}
              className="mt-3 mx-auto flex items-center justify-center gap-1.5 rounded-full bg-tb-cream-2 text-tb-navy border-2 border-tb-cream-3 px-4 h-9 font-hand text-xs tb-tap hover:border-tb-navy/40"
            >
              <Link2 className="w-3.5 h-3.5" />
              Copy link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
